import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { Timeline } from 'primeng/timeline';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin, interval, Subscription } from 'rxjs';

import { ConversationService } from '../service/core/ConversationService.service';
import { MarketingCampaignService } from '../service/core/marketing-campaign.service';
import { LeadDTO, LeadService, LeadVendorDTO } from '../service/core/lead.service';
import { SubscriptionService } from '../service/core/subscription.service';
import { WhatsAppChannelService } from '../service/core/WhatsAppChannelService.service';
import { SopService } from '../service/core/sop.service';
import { TimezoneService } from '../service/core/timezone.service';
import { AppDatePipe } from '../service/core/app-date.pipe';
import { LossReasonDialogComponent } from '../leads/components/shared/loss-reason-dialog/loss-reason-dialog.component';
import { convDisplayId, convInitials, convSubtitle, convTitle, isWidgetConv } from './conversation-display.util';
import { ChatThreadComponent } from './components/chat-thread/chat-thread.component';
import { OperationsService } from '../operations/services/operations.service';

interface GraphEdge {
  fromStatusId: string;
  fromLabel: string;
  toStatusId: string;
  toLabel: string;
  requiresApproval: boolean;
  conditionType: string;
}

interface DagEdge {
  fromId: string;
  toId: string;
  isDone: boolean;
}

interface TreeNode {
  id: string;
  label: string;
  conditionType?: string;
  requiresApproval?: boolean;
  businessState?: string;
  systemState?: string;
  activatedAt?: string | null;
  completedAt?: string | null;
  children: TreeNode[];
  colIndex?: number;
  rowIndex?: number;
  x?: number;
  y?: number;
}

@Component({
  selector: 'app-conversation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    Textarea,
    TagModule,
    TabsModule,
    Timeline,
    TableModule,
    ToastModule,
    TooltipModule,
    ConfirmDialogModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    RouterModule,
    AppDatePipe,
    LossReasonDialogComponent,
    ChatThreadComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './conversation.html',
  styleUrl: './conversation.scss'
})
export class Conversation implements OnDestroy {
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  @ViewChild('convBody') convBody!: ElementRef<HTMLElement>;

  /* ── Column resize ─────────────────────────────────── */
  /* 0.195 = the old 0.15 default widened by 30%. The list carries names, numbers and the
     manager context chips, so the extra room is what stops those chips wrapping. Dragging
     still overrides this, and onMouseMove's 45%-of-body ceiling still applies. */
  sidebarWidth = Math.floor(window.innerWidth * 0.195);
  hideSidebar = false;
  workflowWidth = Math.floor(window.innerWidth * 0.20);
  dragging: 'sidebar' | 'workflow' | null = null;
  private dragStartX = 0;
  private dragStartWidth = 0;

  startDrag(e: MouseEvent, col: 'sidebar' | 'workflow'): void {
    this.dragging = col;
    this.dragStartX = e.clientX;
    this.dragStartWidth = col === 'sidebar' ? this.sidebarWidth : this.workflowWidth;
    e.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (!this.dragging) return;
    const delta = e.clientX - this.dragStartX;
    const bodyW = this.convBody?.nativeElement.offsetWidth ?? window.innerWidth;
    const min = 180, maxShare = bodyW * 0.45;
    if (this.dragging === 'sidebar') {
      this.sidebarWidth = Math.min(maxShare, Math.max(min, this.dragStartWidth + delta));
    } else {
      this.workflowWidth = Math.min(maxShare, Math.max(min, this.dragStartWidth - delta));
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void { this.dragging = null; }

  /* ── Conversation list ─────────────────────────────── */
  conversations: any[] = [];
  loading = false;
  searchTerm = '';
  selected: any = null;
  /* Keyset pagination (infinite scroll) — load newest first, then older pages as the list scrolls. */
  readonly convPageSize = 30;
  private convCursor: string | null = null;   // updatedAt of the last loaded conversation
  convHasMore = true;
  loadingMore = false;
  /* Channel filter — ALL | WHATSAPP | WEB_WIDGET (server-side, so paging stays clean). */
  channelFilter: 'ALL' | 'WHATSAPP' | 'WEB_WIDGET' = 'ALL';

  /* Server-side inbox filters: campaign (recipients of), status (with team vs chat), unread-only.
     The three dropdowns are MULTI-select, so each is a list and EMPTY means "no filter" - there is
     deliberately no 'ALL' option any more, because ticking every box and ticking none must mean
     the same thing. The server ORs the values within each list. */
  campaignFilter: string[] = [];
  statusFilter: ('ESCALATED' | 'CHAT')[] = [];
  unreadOnlyFilter = false;
  /* Whose conversations to show - managers/admins only. A conversation's owner is the assignee
     of its newest ACTIVE case, so a chat with no open case has no owner and is returned by no
     agent filter. Server-enforced: an agent cannot widen their own scope through this. */
  agentFilter: string[] = [];
  /* Which journey's conversations to show. Matched against the journey the row's own chip
     names - the newest ACTIVE case - so the filter can never return a row whose visible chip
     says something else. A chat with no open case belongs to no journey and is excluded by
     any journey selection. */
  journeyFilter: string[] = [];
  campaignOptions: { label: string; value: string }[] = [];
  agentOptions: { label: string; value: string }[] = [];
  journeyOptions: { label: string; value: string }[] = [];
  /** The four dropdowns are collapsed by default — they cost four rows of a panel whose job is
   *  showing conversations. A filter can only be set with the panel open, and the count on the
   *  Filters button keeps it visible once closed, so the list is never narrowed by a control the
   *  user has no sign of. */
  filtersOpen = false;

  /** How many dropdown filters are narrowing the list. Drives the count on the Filters button;
   *  unread has its own visible toggle so it is deliberately not counted here. */
  get activeFilterCount(): number {
    return (this.campaignFilter?.length ? 1 : 0)
         + (this.journeyFilter?.length ? 1 : 0)
         + (this.agentFilter?.length ? 1 : 0)
         + (this.statusFilter?.length ? 1 : 0);
  }
  readonly statusOptions = [
    { label: 'With team', value: 'ESCALATED' },
    { label: 'Bot / chat', value: 'CHAT' }
  ];

  /** Managers + admins see the owner/journey/phase/stage chips and the open-case button;
      plain agents get the simple list (their own chats only, scoped server-side). */
  get isManagerView(): boolean { return !this.conversationService.isAgent(); }

  setChannelFilter(ch: 'ALL' | 'WHATSAPP' | 'WEB_WIDGET'): void {
    if (this.channelFilter === ch) return;
    this.channelFilter = ch;
    this.loadConversations(true);
  }

  /** Any dropdown/toggle filter changed - reload from page one (filters are server-side).
   *
   *  Debounced, because a MULTI-select emits onChange on EVERY option tick while its panel stays
   *  open: a manager ticking five agents would otherwise fire five sequential page-one reloads,
   *  each blanking the list to skeletons. The single-selects this replaced closed on one pick, so
   *  the burst is new. Same 350ms feel as the search box. */
  private filterDebounce: any = null;
  onInboxFilterChange(): void {
    clearTimeout(this.filterDebounce);
    this.filterDebounce = setTimeout(() => this.loadConversations(true), 350);
  }

  clearInboxFilters(): void {
    this.campaignFilter = [];
    this.journeyFilter = [];
    this.filtersOpen = false;
    this.statusFilter = [];
    this.unreadOnlyFilter = false;
    this.agentFilter = [];
    this.loadConversations(true);
  }

  /* Null-tolerant on purpose. PrimeNG's MultiSelect.clear() writes NULL through ngModel rather
     than [], and this getter is bound in the template, so an unguarded `.length` would throw on
     every change-detection pass - including the 5s poll - not just once. The (onClear) handlers
     below restore [] immediately; this guard means a future null writer cannot break the screen. */
  get hasInboxFilters(): boolean {
    return (this.campaignFilter?.length ?? 0) > 0 || (this.statusFilter?.length ?? 0) > 0
        || this.unreadOnlyFilter || (this.agentFilter?.length ?? 0) > 0
        || (this.journeyFilter?.length ?? 0) > 0;
  }

  /** Agent dropdown options. Managers/admins only - a plain agent already sees just their own
   *  chats, so the control would be a no-op. Silent on failure, like the campaign loader. */
  private loadAgentOptions(): void {
    if (!this.isManagerView) return;
    this.conversationService.getAssignableAgents().subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        this.agentOptions = rows
          .filter((u: any) => u?.userId)
          .map((u: any) => ({ label: u.name || u.userId, value: u.userId }));
      },
      error: () => { this.agentOptions = []; }
    });
  }

  /** Journey dropdown options, for AGENTS AND ADMINS ALIKE.
   *
   *  Deliberately /api/operations/available, not the full /api/operations list. That endpoint is
   *  the agent-accessible one, and it already scopes itself by role: a user tied to no team (an
   *  admin) gets every active journey, while an agent gets only the journeys their team actually
   *  handles. So an agent is offered the journeys they can really have conversations in, instead
   *  of a company-wide list where most options would return nothing - and the control appears for
   *  them at all, which the admin-only list endpoint would not have guaranteed.
   *
   *  It returns ACTIVE journeys only, so there is no isActive filtering to do here. Silent on
   *  failure, like the campaign and agent loaders: the filter simply does not render. */
  private loadJourneyOptions(): void {
    this.operationsService.available().subscribe({
      next: (rows: any) => {
        const list: any[] = Array.isArray(rows?.data) ? rows.data : (Array.isArray(rows) ? rows : []);
        this.journeyOptions = list
          .filter((o: any) => o?.id)
          .map((o: any) => ({ label: o.name || o.id, value: o.id }));
      },
      error: () => { this.journeyOptions = []; }
    });
  }

  /** Campaign dropdown options — WhatsApp broadcasts only (that's what lands in this inbox).
      Silent on failure: agents without the campaigns screen simply see no campaign filter. */
  private loadCampaignOptions(): void {
    this.marketingCampaignService.list().subscribe({
      next: (rows: any[]) => {
        this.campaignOptions = (rows || [])
          .filter((c: any) => String(c?.channel || '').toUpperCase() !== 'EMAIL')
          .map((c: any) => ({ label: c.name || c.templateName || c.id, value: c.id }));
      },
      error: () => { this.campaignOptions = []; }
    });
  }

  /* ── New conversation (agent-initiated chat) ─────────────────────────────
   * Search a customer by name / email / mobile, then open a chat with them. The 24h rule is
   * untouched: a customer with no recent inbound shows the closed-window banner and the agent
   * sends an approved template — same flow as any other closed chat. */
  showNewChatDialog = false;
  newChatQuery = '';
  newChatResults: any[] = [];
  newChatSearching = false;
  startingChat = false;
  private newChatSearchTimer: any = null;

  openNewChatDialog(): void {
    this.showNewChatDialog = true;
    this.newChatQuery = '';
    this.newChatResults = [];
    this.newChatSearching = false;
    this.startingChat = false;
  }

  /** Debounced server-side customer search (name / email / mobile). */
  onNewChatQueryChange(): void {
    clearTimeout(this.newChatSearchTimer);
    const q = this.newChatQuery.trim();
    if (q.length < 2) { this.newChatResults = []; this.newChatSearching = false; return; }
    this.newChatSearching = true;
    this.newChatSearchTimer = setTimeout(() => {
      this.conversationService.searchCustomers(q).subscribe({
        next: (res: any) => {
          this.newChatResults = res?.data ?? res ?? [];
          this.newChatSearching = false;
        },
        error: () => { this.newChatResults = []; this.newChatSearching = false; }
      });
    }, 300);
  }

  /** Open (or reuse) the conversation for the picked customer and jump into the chat. */
  startChatWith(row: any): void {
    if (this.startingChat || !row?.waId) return;
    this.startingChat = true;
    this.conversationService.startConversation(row.phone || row.waId).subscribe({
      next: (res: any) => {
        this.startingChat = false;
        this.showNewChatDialog = false;
        const waId = res?.data?.waId ?? row.waId;
        // Reuse the already-loaded card when the chat is in the list; otherwise show a local
        // card for it. (A brand-new chat has no messages yet, so the server list — which only
        // shows conversations WITH messages — will include it after the first template send.)
        let conv = this.conversations.find(c => c.waId === waId);
        if (!conv) {
          conv = {
            waId, name: row.name || null, flowName: 'Normal Chat', status: 'ACTIVE',
            handoff: null, phoneNumberId: null, unreadCount: 0, updatedAt: null,
            channel: 'WHATSAPP', lastMessageAt: null, lastMessageText: null,
            caseId: null, ownerName: null, journeyName: null, phaseName: null, stageName: null
          };
          this.conversations = [conv, ...this.conversations];
        }
        this.selectConversation(conv);
      },
      error: (err: any) => {
        this.startingChat = false;
        const detail = err?.error?.message || err?.message || 'Could not start the chat.';
        this.messageService.add({ severity: 'error', summary: 'New conversation', detail });
      }
    });
  }



  /** Open this chat's ACTIVE case in the full customer-flow screen, in a new tab. */
  openCaseInNewTab(conv: any, ev?: Event): void {
    ev?.stopPropagation();
    if (!conv?.caseId && !conv?.waId) return;
    const seg = conv.waId || conv.caseId;
    const qp = conv.caseId ? `?caseId=${encodeURIComponent(conv.caseId)}` : '';
    window.open(`/customer-flows/${encodeURIComponent(seg)}${qp}`, '_blank');
  }

  channelIcon(ch: string | null | undefined): string {
    return (ch || '').toUpperCase() === 'WEB_WIDGET' ? 'pi-globe' : 'pi-whatsapp';
  }

  /* Labelling lives in conversation-display.util so the list, the thread header and the chat dock
     all answer "what is this chat called?" identically. These thin wrappers exist only because
     Angular templates cannot call a bare imported function. */
  convInitials = convInitials;
  convDisplayId = convDisplayId;
  isWidgetConv = isWidgetConv;
  convTitle = convTitle;
  convSubtitle = convSubtitle;

  /** WhatsApp-style list timestamp (company timezone): time for today, "12 Aug" for older. */
  convTime(iso: any): string {
    if (!iso) return '';
    const day = this.tz.format(iso, 'date');
    if (!day) return '';
    const today = this.tz.format(new Date().toISOString(), 'date');
    return day === today ? this.tz.format(iso, 'time') : this.tz.format(iso, 'dayMonth');
  }

  channel = 'WHATSAPP';

  /* ── Workflow overview ─────────────────────────────── */
  activeTab = 'workflow';
  currentWorkflowName = '';
  automationLabel = '';
  automationMode = '';
  workflowVersion: number | null = null;
  currentStepId = '';
  currentStepType = '';
  currentStepName = '';
  conversationId: any = null;
  events1: any[] = [];
  assignedAgent = 'System';

  /* ── Pipeline progress ─────────────────────────────── */
  pipelineStatuses: any[] = [];
  currentCaseStatusId = '';
  currentCaseStatusName = '';
  loadingPipeline = false;
  private activeWorkflowId = '';

  /* ── Case detail tabs + SOP tasks ──────────────────── */
  caseDetailTab = 'details';
  sopTasks: any[] = [];
  loadingSopTasks = false;
  completingSopTaskId = '';
  expandedTaskId = '';
  taskCommentInputs: Record<string, string> = {};
  addingCommentForTaskId = '';

  /* ── Workflow graph (Tasks tab) ─────────────────────── */
  taskOnlyMode = false;   // true when navigating from My Tasks or Manager View (hides the tree)
  targetStatusId = '';    // status to auto-select when opening Tasks tab from Manager View
  graphRoots: TreeNode[] = [];
  graphColumns: TreeNode[][] = [];
  graphDagEdges: DagEdge[] = [];
  private dagNodeMap = new Map<string, TreeNode>();
  dagTotalW = 0;
  dagTotalH = 0;
  readonly DAG_NODE_W = 120;
  readonly DAG_NODE_H = 30;
  readonly DAG_NODE_GAP_V = 8;
  readonly DAG_COL_GAP = 44;
  loadingGraph = false;
  activeStatusSystemState = '';

  /* ── Status task panel ──────────────────────────────── */
  selectedStatusNode: TreeNode | null = null;
  statusTasks: any[] = [];
  loadingStatusTasks = false;
  taskCommentMap: Record<string, string> = {};
  submittingCommentForTaskId = '';
  completingTaskId = '';

  /* ── Reopen status dialog ───────────────────────────── */
  showReopenDialog = false;
  reopenReason = '';
  reopenSelectedTaskIds: string[] = [];
  reopeningStatus = false;

  /* ── Intervene dialog ───────────────────────────────── */
  showInterveneDialog = false;
  interveneReason = '';
  intervening = false;

  /* ── Workflow cases ────────────────────────────────── */
  workflowCases: { caseId: string; workflowName: string; status: string }[] = [];
  loadingCases = false;
  caseDetail: any = null;
  loadingCaseDetail = false;
  caseNotFound = false;
  selectedCaseStatus = '';
  updatingCaseStatus = false;
  currentCaseId = '';

  /* ── Operation phase status (agent-set business status) ── */
  currentPhaseId = '';
  phaseStatusOptions: any[] = [];
  selectedPhaseStatusId: string | null = null;
  loadingPhaseStatuses = false;
  savingPhaseStatus = false;
  /** Loss-reason dialog state (shown when a terminal non-won status is picked). */
  showLossDialog = false;
  lossDialogStatusName = '';

  /* ── Captured data panel (operation case extractedData) ── */
  caseDataRows: { key: string; value: string }[] = [];
  savingCaseData = false;

  /* ── AI / human mode (manual-mode toggle) ── */
  humanMode = false;
  loadingMode = false;
  togglingMode = false;

  readonly caseStatusOptions = [
    { label: 'Under Review', value: 'UNDER_REVIEW' },
    { label: 'Approved',     value: 'APPROVED' },
    { label: 'Rejected',     value: 'REJECTED' },
    { label: 'Pending',      value: 'PENDING' },
    { label: 'Completed',    value: 'COMPLETED' }
  ];

  loadWorkflowCases(): void {
    if (!this.selected?.waId) return;
    this.loadingCases = true;
    this.conversationService.getWorkflowCases(this.selected.waId).subscribe({
      next: (res: any) => {
        this.workflowCases = Array.isArray(res?.data) ? res.data : [];
        this.loadingCases = false;
      },
      error: () => { this.workflowCases = []; this.loadingCases = false; }
    });
  }

  openCaseDetail(caseItem: any): void {
    const caseId = caseItem.caseId ?? caseItem;
    this.currentCaseId = caseId;
    this.loadingCaseDetail = true;
    this.caseNotFound = false;
    this.caseDetail = null;
    this.pipelineStatuses = [];
    this.currentCaseStatusId = '';
    this.currentPhaseId = '';
    this.phaseStatusOptions = [];
    this.selectedPhaseStatusId = null;
    this.caseDataRows = [];
    // Store status name from the list item immediately
    this.currentCaseStatusName = caseItem.status ?? '';

    this.conversationService.getWorkflowCaseDetail(caseId).subscribe({
      next: (res: any) => {
        this.caseDetail = res?.data ?? null;
        this.selectedCaseStatus = this.caseDetail?.status ?? this.currentCaseStatusName;
        // Operation phase status dropdown — only for operation-governed cases.
        this.currentPhaseId = this.caseDetail?.currentPhaseId ?? '';
        this.selectedPhaseStatusId = this.caseDetail?.currentPhaseStatusId ?? null;
        if (this.currentPhaseId) {
          this.loadPhaseStatuses(this.currentPhaseId);
        }
        // Captured data panel — seed editable rows from the case's extractedData.
        this.caseDataRows = this.toDataRows(this.caseDetail?.extractedData);
        // AI/human mode for the manual-mode toggle.
        this.loadCaseMode();
        // Keep status name from list if detail doesn't provide one
        if (!this.currentCaseStatusName) {
          this.currentCaseStatusName = this.caseDetail?.status ?? '';
        }
        this.loadingCaseDetail = false;
        this.loadCaseComments();

        const workflowId = this.caseDetail?.workflowId
          ?? this.caseDetail?.workflowVersionId
          ?? this.activeWorkflowId;
        if (workflowId) {
          this.loadPipelineProgress(workflowId);
        }
      },
      error: (err: any) => {
        this.loadingCaseDetail = false;
        this.caseNotFound = err?.status === 404 || err?.error?.status === 404;
        this.caseDetail = null;
      }
    });
  }

  updateCaseStatus(): void {
    if (!this.currentCaseId || this.updatingCaseStatus) return;
    this.updatingCaseStatus = true;
    this.conversationService.updateWorkflowCaseStatus(this.currentCaseId).subscribe({
      next: (res: any) => {
        this.caseDetail = res?.data ?? this.caseDetail;
        this.updatingCaseStatus = false;
        this.loadWorkflowCases();
        this.messageService.add({ severity: 'success', summary: 'Stage Advanced', detail: 'Case moved to the next stage.' });
      },
      error: () => {
        this.updatingCaseStatus = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not advance the stage. Please try again.' });
      }
    });
  }

  advancingPhase = false;

  /** Finish the current (manual) phase and advance the operation to its next
      phase. The engine starts the next phase's workflow (auto) or hands off
      again (manual). */
  advanceOperationPhase(): void {
    if (!this.currentCaseId || this.advancingPhase) return;
    this.advancingPhase = true;
    this.conversationService.advancePhase(this.currentCaseId).subscribe({
      next: () => {
        this.advancingPhase = false;
        this.messageService.add({ severity: 'success', summary: 'Phase advanced', detail: 'Moved the operation to its next phase.' });
        this.loadWorkflowCases();
      },
      error: () => {
        this.advancingPhase = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not advance the phase. Please try again.' });
      }
    });
  }

  /** Load the business statuses configured for this case's live phase. */
  loadPhaseStatuses(phaseId: string): void {
    this.loadingPhaseStatuses = true;
    this.conversationService.getPhaseStatuses(phaseId).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        this.phaseStatusOptions = list.filter((s: any) => s?.isActive !== false);
        this.loadingPhaseStatuses = false;
      },
      error: () => { this.phaseStatusOptions = []; this.loadingPhaseStatuses = false; }
    });
  }

  /** Persist the agent-selected stage. A terminal stage auto-advances the operation to its
      next phase (engine side). A stage configured as a LOST deal first prompts "why?" — the
      stage applies either way once the dialog resolves (Save or Skip).

      The loss test is caseOutcome === 'LOST', NOT the retired "terminal and not won" derivation.
      Those are different questions now: terminal only auto-advances, so the old test both fired
      on ordinary move-on stages and stayed silent on real losses — including "Closed Lost",
      which every new tenant is seeded with as caseOutcome=LOST and terminal=false. */
  setPhaseStatus(): void {
    if (!this.currentCaseId || !this.selectedPhaseStatusId || this.savingPhaseStatus) return;
    const pick = this.phaseStatusOptions.find((s: any) => s.id === this.selectedPhaseStatusId);
    if (pick?.caseOutcome === 'LOST') {
      this.lossDialogStatusName = pick.name || 'lost';
      this.showLossDialog = true;
      return;   // continues in onLossReasonConfirmed()
    }
    this.applyPhaseStatus();
  }

  /** Loss dialog resolved (Save or Skip) — apply the status with the context. */
  onLossReasonConfirmed(r: { reasonCode: string | null; reasonNote: string }): void {
    this.applyPhaseStatus({ reasonCode: r.reasonCode, reasonNote: r.reasonNote });
  }

  private applyPhaseStatus(loss?: { reasonCode?: string | null; reasonNote?: string | null }): void {
    if (!this.currentCaseId || !this.selectedPhaseStatusId || this.savingPhaseStatus) return;
    this.savingPhaseStatus = true;
    this.conversationService.setCasePhaseStatus(this.currentCaseId, this.selectedPhaseStatusId, loss).subscribe({
      next: () => {
        this.savingPhaseStatus = false;
        const picked = this.phaseStatusOptions.find((s: any) => s.id === this.selectedPhaseStatusId);
        this.messageService.add({ severity: 'success', summary: 'Stage updated', detail: picked?.name ? `Stage set to "${picked.name}".` : 'Case stage updated.' });
        // A terminal stage moves the operation on; refresh the case list.
        if (picked?.isTerminal) {
          this.loadWorkflowCases();
        }
      },
      error: () => {
        this.savingPhaseStatus = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not update the status. Please try again.' });
      }
    });
  }

  /** Convert the case's extractedData object into editable key/value rows. */
  private toDataRows(obj: any): { key: string; value: string }[] {
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj).map(k => ({ key: k, value: this.stringifyVal(obj[k]) }));
  }

  private stringifyVal(v: any): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return String(v); } }
    return String(v);
  }

  /** Add a blank captured-data row (agent enters key + value, then Save). */
  addCaseDataField(): void {
    this.caseDataRows = [...this.caseDataRows, { key: '', value: '' }];
  }

  /** Load whether an agent has taken over this conversation (AI paused). */
  loadCaseMode(): void {
    if (!this.currentCaseId) return;
    this.loadingMode = true;
    this.conversationService.getCaseMode(this.currentCaseId).subscribe({
      next: (res: any) => {
        const v = res?.data ?? res;
        this.humanMode = !!(v && v.human);
        this.loadingMode = false;
      },
      error: () => { this.loadingMode = false; }
    });
  }

  /** Flip AI ↔ human. Taking over pauses the AI (replies route to the agent);
      handing back resumes AI handling. */
  toggleMode(): void {
    if (!this.currentCaseId || this.togglingMode) return;
    const next = !this.humanMode;
    this.togglingMode = true;
    this.conversationService.setCaseMode(this.currentCaseId, next).subscribe({
      next: () => {
        this.humanMode = next;
        this.togglingMode = false;
        this.messageService.add({
          severity: 'success',
          summary: next ? 'You have taken over' : 'Handed back to AI',
          detail: next ? 'The AI is paused — replies route to you.' : 'The AI will handle replies again.'
        });
      },
      error: () => {
        this.togglingMode = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not change the mode. Please try again.' });
      }
    });
  }

  /** Persist the captured-data rows. Merges into the case's extractedData
      (server-side merge — saving doesn't delete keys you removed locally). */
  saveCaseData(): void {
    if (!this.currentCaseId || this.savingCaseData) return;
    const data: Record<string, any> = {};
    for (const r of this.caseDataRows) {
      const k = (r.key || '').trim();
      if (k) data[k] = r.value;
    }
    this.savingCaseData = true;
    this.conversationService.setCaseData(this.currentCaseId, data).subscribe({
      next: () => {
        this.savingCaseData = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Captured data updated.' });
      },
      error: () => {
        this.savingCaseData = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not save the data. Please try again.' });
      }
    });
  }

  closeCaseDetail(): void {
    this.caseDetail = null;
    this.caseNotFound = false;
    this.caseComments = [];
    this.newComment = '';
    this.pipelineStatuses = [];
    this.currentCaseStatusId = '';
    this.currentCaseStatusName = '';
    this.currentPhaseId = '';
    this.phaseStatusOptions = [];
    this.selectedPhaseStatusId = null;
    this.caseDataRows = [];
    this.savingCaseData = false;
    this.humanMode = false;
    this.loadingMode = false;
    this.togglingMode = false;
    this.caseDetailTab = 'details';
    this.sopTasks = [];
    this.expandedTaskId = '';
    this.taskCommentInputs = {};
    this.taskOnlyMode = false;
    this.targetStatusId = '';
    this.graphRoots = [];
    this.graphColumns = [];
    this.graphDagEdges = [];
    this.dagNodeMap.clear();
    this.dagTotalW = 0;
    this.dagTotalH = 0;
    this.loadingGraph = false;
    this.activeStatusSystemState = '';
    this.selectedStatusNode = null;
    this.statusTasks = [];
    this.loadingStatusTasks = false;
    this.taskCommentMap = {};
    this.submittingCommentForTaskId = '';
    this.completingTaskId = '';
    this.showReopenDialog = false;
    this.reopenReason = '';
    this.reopenSelectedTaskIds = [];
    this.reopeningStatus = false;
    this.showInterveneDialog = false;
    this.interveneReason = '';
    this.intervening = false;
  }

  switchCaseTab(tab: string): void {
    this.caseDetailTab = tab;
    if (tab === 'tasks') {
      this.loadCaseGraph();
    }
  }

  loadCaseGraph(): void {
    if (!this.currentCaseId) return;
    this.loadingGraph = true;
    this.graphColumns = [];
    this.graphDagEdges = [];
    forkJoin({
      edges: this.conversationService.getCaseGraph(this.currentCaseId),
      statuses: this.conversationService.getCaseStatuses(this.currentCaseId)
    }).subscribe({
      next: ({ edges, statuses }) => {
        const statusMap = new Map<string, any>();
        (statuses as any[]).forEach((s: any) => statusMap.set(s.statusId, s));
        const active = (statuses as any[]).find((s: any) => s.businessState === 'ACTIVE');
        this.activeStatusSystemState = active?.systemState ?? '';
        this.buildDagLayout(edges as GraphEdge[], statusMap);
        this.loadingGraph = false;
        // Auto-select: prefer the targeted status (from Manager View), fall back to ACTIVE node
        const allNodes = this.graphColumns.flat();
        const autoNode = (this.targetStatusId && allNodes.find(n => n.id === this.targetStatusId))
          || allNodes.find(n => n.businessState === 'ACTIVE');
        if (autoNode) this.onGraphNodeClick(autoNode);
      },
      error: () => {
        this.graphColumns = [];
        this.graphDagEdges = [];
        this.loadingGraph = false;
      }
    });
  }

  onGraphNodeClick(node: TreeNode): void {
    this.selectedStatusNode = node;
    this.statusTasks = [];
    this.loadingStatusTasks = true;
    this.conversationService.getCaseStatusTasks(this.currentCaseId, node.id).subscribe({
      next: (tasks: any[]) => {
        this.statusTasks = Array.isArray(tasks) ? tasks : [];
        this.loadingStatusTasks = false;
      },
      error: () => {
        this.statusTasks = [];
        this.loadingStatusTasks = false;
      }
    });
  }

  onCompleteTask(task: any): void {
    if (task.state === 'COMPLETED' || this.completingTaskId === task.taskId) return;
    this.confirmationService.confirm({
      message: `Mark "<strong>${task.name || task.taskName}</strong>" as completed?`,
      header: 'Complete Task',
      icon: 'pi pi-check-circle',
      acceptLabel: 'Yes, complete',
      rejectLabel: 'Cancel',
      accept: () => {
        this.completingTaskId = task.taskId;
        this.conversationService.completeTask(this.currentCaseId, task.taskId).subscribe({
          next: () => {
            task.state = 'COMPLETED';
            this.completingTaskId = '';
            this.messageService.add({ severity: 'success', summary: 'Task Completed', detail: `"${task.name || task.taskName}" marked as done.` });
          },
          error: () => {
            this.completingTaskId = '';
            this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not complete task.' });
          }
        });
      }
    });
  }

  submitTaskComment(task: any): void {
    const comment = (this.taskCommentMap[task.taskId] ?? '').trim();
    if (!comment || this.submittingCommentForTaskId === task.taskId) return;
    this.submittingCommentForTaskId = task.taskId;
    this.conversationService.addSopTaskComment(this.currentCaseId, task.taskId, comment).subscribe({
      next: () => {
        if (!task.comments) task.comments = [];
        task.comments.push({ comment, createdBy: '', createdAt: new Date().toISOString() });
        this.taskCommentMap[task.taskId] = '';
        this.submittingCommentForTaskId = '';
        this.messageService.add({ severity: 'success', summary: 'Comment Added', detail: 'Comment saved successfully.' });
      },
      error: () => {
        this.submittingCommentForTaskId = '';
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not add comment.' });
      }
    });
  }

  openReopenDialog(): void {
    this.reopenReason = '';
    this.reopenSelectedTaskIds = [];
    this.showReopenDialog = true;
  }

  toggleReopenTask(taskId: string): void {
    const idx = this.reopenSelectedTaskIds.indexOf(taskId);
    if (idx === -1) this.reopenSelectedTaskIds.push(taskId);
    else this.reopenSelectedTaskIds.splice(idx, 1);
  }

  isReopenTaskSelected(taskId: string): boolean {
    return this.reopenSelectedTaskIds.includes(taskId);
  }

  submitReopen(): void {
    if (!this.currentCaseId || !this.selectedStatusNode || this.reopeningStatus) return;
    if (!this.reopenReason.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Reason required', detail: 'Please enter a reason to reopen.' });
      return;
    }
    if (!this.reopenSelectedTaskIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'Select tasks', detail: 'Please select at least one task to reopen.' });
      return;
    }
    this.confirmationService.confirm({
      message: 'Are you sure you want to reopen this stage? The selected tasks will be reset.',
      header: 'Confirm Reopen',
      icon: 'pi pi-replay',
      acceptLabel: 'Yes, Reopen',
      rejectLabel: 'Cancel',
      accept: () => {
        this.reopeningStatus = true;
        this.conversationService.reopenStatus(
          this.currentCaseId,
          this.selectedStatusNode!.id,
          this.reopenReason.trim(),
          this.reopenSelectedTaskIds
        ).subscribe({
          next: () => {
            this.reopeningStatus = false;
            this.showReopenDialog = false;
            this.messageService.add({ severity: 'success', summary: 'Reopened', detail: 'Status reopened successfully.' });
            this.loadCaseGraph();
          },
          error: (err: any) => {
            this.reopeningStatus = false;
            const msg = err?.error?.message ?? 'Could not reopen the status.';
            this.messageService.add({ severity: 'error', summary: 'Failed', detail: msg });
          }
        });
      }
    });
  }

  submitIntervene(): void {
    if (!this.currentCaseId || !this.selectedStatusNode || this.intervening) return;
    if (!this.interveneReason.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Reason required', detail: 'Please enter a reason for intervention.' });
      return;
    }
    this.confirmationService.confirm({
      message: 'Are you sure you want to trigger a manual intervention for this stage?',
      header: 'Confirm Intervention',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Intervene',
      rejectLabel: 'Cancel',
      accept: () => {
        this.intervening = true;
        this.conversationService.interveneStatus(
          this.currentCaseId,
          this.selectedStatusNode!.id,
          this.interveneReason.trim()
        ).subscribe({
          next: () => {
            this.intervening = false;
            this.showInterveneDialog = false;
            this.interveneReason = '';
            this.messageService.add({ severity: 'success', summary: 'Intervention Triggered', detail: 'Manual intervention has been recorded.' });
            this.loadCaseGraph();
          },
          error: (err: any) => {
            this.intervening = false;
            const msg = err?.error?.message ?? 'Could not trigger intervention.';
            this.messageService.add({ severity: 'error', summary: 'Failed', detail: msg });
          }
        });
      }
    });
  }

  advanceCaseStage(): void {
    if (!this.currentCaseId || !this.selectedStatusNode || this.updatingCaseStatus) return;
    this.updatingCaseStatus = true;
    this.conversationService.approveStatus(this.currentCaseId, this.selectedStatusNode.id).subscribe({
      next: () => {
        this.updatingCaseStatus = false;
        this.messageService.add({ severity: 'success', summary: 'Stage Approved', detail: 'Status approved successfully.' });
        this.loadWorkflowCases();
        this.loadCaseGraph();
        this.selectedStatusNode = null;
        this.statusTasks = [];
      },
      error: () => {
        this.updatingCaseStatus = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not approve the status.' });
      }
    });
  }

  buildDagLayout(edges: GraphEdge[], statusMap: Map<string, any> = new Map()): void {
    if (!edges.length) {
      this.graphColumns = [];
      this.graphDagEdges = [];
      this.dagTotalW = 0;
      this.dagTotalH = 0;
      return;
    }

    // Build node registry + adjacency
    const nodeMap = new Map<string, TreeNode>();
    const inDegree = new Map<string, number>();
    const outEdges = new Map<string, string[]>();

    for (const edge of edges) {
      for (const [id, label] of [[edge.fromStatusId, edge.fromLabel], [edge.toStatusId, edge.toLabel]] as [string, string][]) {
        if (!nodeMap.has(id)) {
          const s = statusMap.get(id);
          nodeMap.set(id, { id, label, children: [], businessState: s?.businessState, systemState: s?.systemState, activatedAt: s?.activatedAt, completedAt: s?.completedAt });
          inDegree.set(id, 0);
          outEdges.set(id, []);
        }
      }
      nodeMap.get(edge.fromStatusId)!.label = edge.fromLabel;
      const toNode = nodeMap.get(edge.toStatusId)!;
      toNode.label = edge.toLabel;
      toNode.conditionType = edge.conditionType;
      toNode.requiresApproval = edge.requiresApproval;
      inDegree.set(edge.toStatusId, (inDegree.get(edge.toStatusId) ?? 0) + 1);
      outEdges.get(edge.fromStatusId)!.push(edge.toStatusId);
    }

    // Kahn's BFS — assign max-depth column index to each node
    const colIndex = new Map<string, number>();
    const inDegTemp = new Map<string, number>(inDegree);
    const queue: string[] = [];
    for (const [id, deg] of inDegTemp) {
      if (deg === 0) { colIndex.set(id, 0); queue.push(id); }
    }
    let head = 0;
    while (head < queue.length) {
      const id = queue[head++];
      const myCol = colIndex.get(id)!;
      for (const childId of outEdges.get(id) ?? []) {
        const proposed = myCol + 1;
        if (!colIndex.has(childId) || colIndex.get(childId)! < proposed) colIndex.set(childId, proposed);
        inDegTemp.set(childId, inDegTemp.get(childId)! - 1);
        if (inDegTemp.get(childId) === 0) queue.push(childId);
      }
    }

    // Group into columns
    const maxCol = Math.max(...colIndex.values());
    const columns: TreeNode[][] = Array.from({ length: maxCol + 1 }, () => []);
    for (const [id, col] of colIndex) {
      const node = nodeMap.get(id)!;
      node.colIndex = col;
      columns[col].push(node);
    }
    columns.forEach(col => col.forEach((n, i) => { n.rowIndex = i; }));

    // Pre-compute pixel positions (vertically centred per column)
    const maxNodes = Math.max(...columns.map(c => c.length));
    const slotH = this.DAG_NODE_H + this.DAG_NODE_GAP_V;
    const totalH = maxNodes * slotH - this.DAG_NODE_GAP_V;
    for (const col of columns) {
      const colH = col.length * slotH - this.DAG_NODE_GAP_V;
      const offsetY = (totalH - colH) / 2;
      for (const node of col) {
        node.x = node.colIndex! * (this.DAG_NODE_W + this.DAG_COL_GAP);
        node.y = offsetY + node.rowIndex! * slotH;
      }
    }

    this.dagNodeMap = nodeMap;
    this.graphColumns = columns;
    this.dagTotalW = (maxCol + 1) * (this.DAG_NODE_W + this.DAG_COL_GAP) - this.DAG_COL_GAP + 8;
    this.dagTotalH = totalH + 8;
    this.graphDagEdges = edges.map(e => ({
      fromId: e.fromStatusId,
      toId: e.toStatusId,
      isDone: nodeMap.get(e.fromStatusId)?.businessState === 'COMPLETED'
    }));
  }

  getDagEdgePath(edge: DagEdge): string {
    const from = this.dagNodeMap.get(edge.fromId);
    const to = this.dagNodeMap.get(edge.toId);
    if (!from || from.x == null || !to || to.x == null) return '';
    const x1 = from.x + this.DAG_NODE_W;
    const y1 = from.y! + this.DAG_NODE_H / 2;
    const x2 = to.x;
    const y2 = to.y! + this.DAG_NODE_H / 2;
    const cx = (x1 + x2) / 2;
    return `M ${x1},${y1} C ${cx},${y1} ${cx},${y2} ${x2},${y2}`;
  }

  loadSopTasks(): void {
    if (!this.currentCaseId || !this.currentCaseStatusId) return;
    this.loadingSopTasks = true;
    this.sopService.getCaseSopTasks(this.currentCaseId, this.currentCaseStatusId).subscribe({
      next: (res: any) => {
        this.sopTasks = res?.data?.tasks ?? res?.tasks ?? [];
        this.loadingSopTasks = false;
      },
      error: () => { this.sopTasks = []; this.loadingSopTasks = false; }
    });
  }

  onTaskCheck(task: any, checked: boolean): void {
    // Only act when user checks (not unchecks), and task is not already done
    if (!checked || task.isCompleted) return;

    this.confirmationService.confirm({
      message: `Mark "<strong>${task.label}</strong>" as completed?`,
      header: 'Complete Task',
      icon: 'pi pi-check-circle',
      acceptLabel: 'Yes, complete',
      rejectLabel: 'Cancel',
      accept: () => {
        this.completingSopTaskId = task.taskId;
        this.sopService.completeSopTask(this.currentCaseId, task.taskId).subscribe({
          next: () => {
            task.isCompleted = true;
            this.completingSopTaskId = '';
            this.messageService.add({ severity: 'success', summary: 'Task Completed', detail: `"${task.label}" marked as done.` });
          },
          error: () => {
            this.completingSopTaskId = '';
            this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not complete task. Please try again.' });
          }
        });
      }
    });
  }

  toggleTaskComments(taskId: string): void {
    this.expandedTaskId = this.expandedTaskId === taskId ? '' : taskId;
  }

  addTaskComment(task: any): void {
    const comment = (this.taskCommentInputs[task.taskId] ?? '').trim();
    if (!comment || this.addingCommentForTaskId === task.taskId) return;
    this.addingCommentForTaskId = task.taskId;
    this.sopService.addTaskComment(this.currentCaseId, task.taskId, comment).subscribe({
      next: () => {
        if (!task.comments) task.comments = [];
        task.comments.push({ comment, createdAt: new Date().toISOString() });
        this.taskCommentInputs[task.taskId] = '';
        this.addingCommentForTaskId = '';
        this.messageService.add({ severity: 'success', summary: 'Comment Added', detail: 'Comment saved successfully.' });
      },
      error: () => {
        this.addingCommentForTaskId = '';
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not add comment.' });
      }
    });
  }

  /* ── Case comments ─────────────────────────────────── */
  caseComments: any[] = [];
  loadingComments = false;
  newComment = '';
  addingComment = false;

  loadCaseComments(): void {
    if (!this.currentCaseId) return;
    this.loadingComments = true;
    this.conversationService.getCaseComments(this.currentCaseId).subscribe({
      next: (res: any) => {
        this.caseComments = Array.isArray(res?.data) ? res.data : [];
        this.loadingComments = false;
      },
      error: () => { this.caseComments = []; this.loadingComments = false; }
    });
  }

  addComment(): void {
    if (!this.newComment.trim() || this.addingComment) return;
    this.addingComment = true;
    this.conversationService.addCaseComment(this.currentCaseId, this.newComment.trim()).subscribe({
      next: () => {
        this.newComment = '';
        this.addingComment = false;
        this.loadCaseComments();
        this.messageService.add({ severity: 'success', summary: 'Comment Added', detail: 'Your comment was added successfully.' });
      },
      error: () => {
        this.addingComment = false;
        this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Could not add comment. Please try again.' });
      }
    });
  }

  get extractedDataEntries(): { key: string; value: string }[] {
    const d = this.caseDetail?.extractedData;
    if (!d || typeof d !== 'object') return [];
    return Object.entries(d).map(([key, value]) => ({ key, value: String(value ?? '') }));
  }

  /* ── Leads / Vendor ────────────────────────────────── */
  leads: LeadDTO[] = [];
  vendorReplies: LeadVendorDTO[] = [];
  selectedLead: LeadDTO | null = null;
  showVendorsDialog = false;
  loadingLeads = false;
  loadingVendors = false;
  leadActionLoading = false;

  private conversationService = inject(ConversationService);
  private marketingCampaignService = inject(MarketingCampaignService);
  private operationsService = inject(OperationsService);
  private subscriptionService = inject(SubscriptionService);
  private leadService = inject(LeadService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private channelService = inject(WhatsAppChannelService);
  private sopService = inject(SopService);
  private route = inject(ActivatedRoute);
  private tz = inject(TimezoneService);


  ngOnInit(): void {
    this.loadConversations();
    this.loadCampaignOptions();
    this.loadJourneyOptions();
    this.loadAgentOptions();
    this.route.queryParams.subscribe((params: Record<string, string>) => {
      const waId = params['waId'];
      const caseId = params['caseId'];
      const shouldHideSidebar = params['hideSidebar'] === 'true';
      const fromTask = params['fromTask'] === 'true';
      const fromManager = params['fromManager'] === 'true';
      const statusId = params['statusId'] ?? '';
      if (!waId && !caseId) return;

      if (shouldHideSidebar) this.hideSidebar = true;
      if (fromTask) this.taskOnlyMode = true;
      if (fromManager && statusId) this.targetStatusId = statusId;

      const afterCaseOpen = () => {
        if (fromTask || fromManager) this.switchCaseTab('tasks');
      };

      if (waId) {
        const trySelect = () => {
          if (this.loading) { setTimeout(trySelect, 200); return; }
          const match = this.conversations.find(c => c.waId === waId);
          if (!match) return;
          this.selectConversation(match);
          if (caseId) {
            this.activeTab = 'cases';
            const tryOpenCase = () => {
              if (this.loadingCases) { setTimeout(tryOpenCase, 200); return; }
              const caseItem = this.workflowCases.find((c: any) => c.caseId === caseId);
              this.openCaseDetail(caseItem ?? { caseId });
              afterCaseOpen();
            };
            tryOpenCase();
          }
        };
        trySelect();
      } else if (caseId) {
        this.activeTab = 'cases';
        this.openCaseDetail({ caseId });
        afterCaseOpen();
      }
    });
  }

  ngOnDestroy(): void {
    // Scrolling and message polling moved into <app-chat-thread>, which tears down its own.
  }

  /* ── List ──────────────────────────────────────────── */
  /* Monotonic token for page-one loads. Filter changes can overlap (a debounced reload racing a
     manual Refresh), and the LAST request issued must win regardless of which response lands
     first - otherwise the list can settle on results for a filter the user already moved off. */
  private convLoadSeq = 0;

  loadConversations(reset = true): void {
    const seq = reset ? ++this.convLoadSeq : this.convLoadSeq;
    if (reset) {
      this.loading = true;
      this.convCursor = null;
      this.convHasMore = true;
    } else {
      if (!this.convHasMore || this.loadingMore || this.loading) return;
      this.loadingMore = true;
    }
    const before = reset ? undefined : (this.convCursor ?? undefined);
    const ch = this.channelFilter === 'ALL' ? undefined : this.channelFilter;
    this.conversationService.getConversations(this.convPageSize, before, ch, {
      campaignIds: this.campaignFilter,
      operationIds: this.journeyFilter,
      statuses: this.statusFilter,
      unreadOnly: this.unreadOnlyFilter,
      q: this.searchTerm,
      assignedUserIds: this.agentFilter
    }).subscribe({
      next: (data: any) => {
        // A newer page-one load has been issued since this one left - drop the stale answer.
        if (seq !== this.convLoadSeq) return;
        const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);
        const mapped = rows.map((item: any) => ({
          waId: item.waId,
          flowName: item.flowName ? item.flowName.replace(/_/g, ' ') : 'Normal Chat',
          status: item.status,
          currentStep: item.currentStepKey,
          handoff: item.handoffStatus,
          phoneNumberId: item.phoneNumberId,
          unreadCount: item.unreadCount ?? 0,
          updatedAt: item.updatedAt ?? null,
          channel: item.channel ?? null,
          name: item.name ?? null,
          contactPhone: item.contactPhone ?? null,
          lastMessageAt: item.lastMessageAt ?? null,
          lastMessageText: item.lastMessageText ?? null,
          caseId: item.caseId ?? null,
          ownerName: item.ownerName ?? null,
          journeyName: item.journeyName ?? null,
          phaseName: item.phaseName ?? null,
          stageName: item.stageName ?? null
        }));
        if (reset) {
          this.conversations = mapped;
        } else {
          // Append + dedup by waId (a keyset tie can re-emit the boundary row).
          const seen = new Set(this.conversations.map(c => c.waId));
          this.conversations.push(...mapped.filter(m => !seen.has(m.waId)));
        }
        if (mapped.length) this.convCursor = mapped[mapped.length - 1].updatedAt;
        this.convHasMore = rows.length >= this.convPageSize;
        this.loading = false;
        this.loadingMore = false;
      },
      error: () => { this.loading = false; this.loadingMore = false; }
    });
  }

  /** Infinite scroll — pull the next older page when the list nears the bottom. */
  onSidebarScroll(ev: Event): void {
    const el = ev.target as HTMLElement;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) this.loadConversations(false);
  }

  /** The list as the SERVER filtered it — search runs server-side over ALL conversations
   *  (number + lead name/email), so no client-side re-filter that could hide server matches. */
  get filteredConversations(): any[] {
    return this.conversations;
  }

  /** Debounced server-side search — reloads page one with `q` on each keystroke pause. */
  private searchDebounce: any = null;
  onSearchTermChange(): void {
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.loadConversations(true), 350);
  }

  /** The thread flipped AI <-> team; mirror it on the list row so the badge matches. */
  onThreadHandoffChanged(status: string): void {
    if (this.selected) this.selected.handoff = status;
  }

  selectConversation(conv: any): void {
    if (this.selected?.waId === conv.waId) return;
    this.selected = conv;
    this.resetDetailState();
    // No loadMessages/checkWindow/startPolling here any more: <app-chat-thread> reacts to the new
    // [conversation] and does all three itself, so the two can never drift out of step.
    // Shared read marker — opening the chat clears its unread badge for everyone. Optimistic locally.
    if (conv.unreadCount) conv.unreadCount = 0;
    if (conv.waId) this.conversationService.markConversationRead(conv.waId).subscribe({ error: () => {} });
  }

  private resetDetailState(): void {
    // The chat column and the 24h window/template state reset themselves inside
    // <app-chat-thread> when [conversation] changes - this method is now workflow-rail only.

    // Workflow summary
    this.events1 = [];
    this.conversationId = null;
    this.currentWorkflowName = '';
    this.automationLabel = '';
    this.automationMode = '';
    this.workflowVersion = null;
    this.currentStepId = '';
    this.currentStepName = '';
    this.currentStepType = '';
    this.activeWorkflowId = '';

    // Leads
    this.leads = [];

    // Workflow cases list
    this.workflowCases = [];
    this.loadingCases = false;

    // Case detail
    this.caseDetail = null;
    this.caseNotFound = false;
    this.loadingCaseDetail = false;
    this.currentCaseId = '';
    this.selectedCaseStatus = '';
    this.updatingCaseStatus = false;
    this.caseComments = [];
    this.newComment = '';
    this.addingComment = false;

    // Pipeline
    this.pipelineStatuses = [];
    this.currentCaseStatusId = '';
    this.currentCaseStatusName = '';
    this.loadingPipeline = false;

    // Case detail tabs + SOP tasks
    this.caseDetailTab = 'details';
    this.sopTasks = [];
    this.loadingSopTasks = false;
    this.completingSopTaskId = '';
    this.expandedTaskId = '';
    this.taskCommentInputs = {};
    this.addingCommentForTaskId = '';
    this.graphRoots = [];
    this.loadingGraph = false;
    this.activeStatusSystemState = '';
    this.selectedStatusNode = null;
    this.statusTasks = [];
    this.loadingStatusTasks = false;
    this.taskCommentMap = {};
    this.submittingCommentForTaskId = '';
    this.completingTaskId = '';

    // UI state
    this.activeTab = 'workflow';
  }

  getSeverity(status: string): string {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE':    return 'success';
      case 'COMPLETED': return 'info';
      default:          return 'secondary';
    }
  }

  /* ── Workflow progress ─────────────────────────────── */
  loadWorkflowProgress(): void {
    if (!this.selected) return;
    this.conversationService
      .getWorkflowProgress(this.selected.waId, this.selected.phoneNumberId)
      .subscribe({
        next: (res: any) => {
          this.currentWorkflowName = res.workflow?.flowName ?? '';
          this.workflowVersion = res.workflow?.version ?? null;
          this.automationLabel =
            res.automation?.status === 'ACTIVE' ? 'Waiting for Reply' : (res.automation?.status ?? '');
          this.automationMode = res.automation?.mode ?? '';
          this.currentStepId   = res.currentStep?.stepId ?? '';
          this.currentStepName = res.currentStep?.name ?? '';
          this.currentStepType = res.currentStep?.type ?? '';
          this.conversationId  = res.conversationId;
          this.activeWorkflowId = res.workflow?.workflowId ?? '';
          this.loadLeads();
          this.events1 = (res.steps ?? []).map((step: any) => ({
            status: step.status ?? 'PENDING',
            label: step.stepId + (step.type ? ' (' + step.type + ')' : ''),
            date: step.executedAt ? this.tz.format(step.executedAt, 'datetime') : '',
            icon: step.status === 'COMPLETED' ? 'pi pi-check-circle'
                : step.status === 'CURRENT'   ? 'pi pi-circle-fill'
                : 'pi pi-circle',
            color: step.status === 'COMPLETED' ? '#22c55e'
                 : step.status === 'CURRENT'   ? '#3b82f6'
                 : '#cbd5e1'
          }));

        },
        error: () => {
          this.currentWorkflowName = '-';
          this.automationLabel = '-';
          this.automationMode = '-';
          this.workflowVersion = null;
          this.currentStepId = '';
          this.currentStepName = '';
          this.currentStepType = '';
          this.events1 = [];
        }
      });
  }

  loadPipelineProgress(workflowId: string): void {
    this.loadingPipeline = true;
    this.channelService.getWorkflowPipeline(workflowId).subscribe({
      next: (res: any) => {
        this.loadingPipeline = false;
        const raw: any[] = res?.data ?? res ?? [];
        this.pipelineStatuses = raw.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        // Match the case's status name (from list) to a pipeline stage id
        const matched = this.pipelineStatuses.find(
          s => s.name?.toLowerCase() === this.currentCaseStatusName.toLowerCase()
        );
        this.currentCaseStatusId = matched?.id ?? '';
      },
      error: () => { this.loadingPipeline = false; }
    });
  }

  getActiveStageIndex(): number {
    return this.pipelineStatuses.findIndex(s => s.id === this.currentCaseStatusId);
  }

  isStageCompleted(statusId: string): boolean {
    const activeIdx = this.getActiveStageIndex();
    const idx = this.pipelineStatuses.findIndex(s => s.id === statusId);
    return activeIdx >= 0 && idx < activeIdx;
  }

  /* ── Messages ──────────────────────────────────────── */
  /* Like convLoadSeq for the list: a slow response for the PREVIOUS conversation must not
     overwrite the one the user just opened (and re-arm its scroll). */


  private getConversationChannel(): string {
    const id = typeof this.conversationId === 'string' ? this.conversationId.trim() : '';
    return id.length > 12 ? 'WEB_WIDGET' : this.channel;
  }
















  /* ── Message helpers ───────────────────────────────── */



  /* ── Media rendering ───────────────────────────────────────────────────────
   * Inbound WhatsApp media is already downloaded + self-hosted at ingest time, so `content.mediaUrl`
   * is a directly-loadable public URL (both inbound `/uploads/...` and outbound CDN/template URLs).
   * The browser loads `<img>/<audio>/<video>` natively — no proxy, no auth fetch, no blob. */






























  getSenderInitials(msg: any): string {
    const s = msg?.senderPhoneNumber || msg?.from || '';
    return String(s).slice(-2) || 'U';
  }

  /* ── Leads ─────────────────────────────────────────── */
  loadLeads(): void {
    if (!this.conversationId) return;
    this.loadingLeads = true;
    this.leadService.getLeads(this.conversationId).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : [];
        this.leads = rows.sort((a, b) =>
          (b?.createdAt ? new Date(b.createdAt).getTime() : 0) -
          (a?.createdAt ? new Date(a.createdAt).getTime() : 0)
        );
        this.loadingLeads = false;
      },
      error: () => { this.leads = []; this.loadingLeads = false; }
    });
  }

  viewVendors(lead: LeadDTO): void {
    if (!lead?.id) return;
    this.selectedLead = lead;
    this.showVendorsDialog = true;
    this.loadingVendors = true;
    this.leadService.getLeadVendors(lead.id).subscribe({
      next: (res) => { this.vendorReplies = Array.isArray(res) ? res : []; this.loadingVendors = false; },
      error: () => {
        this.vendorReplies = [];
        this.loadingVendors = false;
        this.messageService.add({ severity: 'error', summary: 'Vendor Replies', detail: 'Failed to fetch vendor replies.' });
      }
    });
  }

  assignVendor(leadId: number, vendorId: number): void {
    this.confirmationService.confirm({
      message: `Assign vendor #${vendorId} to lead #${leadId}?`,
      header: 'Confirm Assignment',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.leadActionLoading = true;
        this.leadService.assignLead({ leadId, vendorId }).subscribe({
          next: () => {
            this.leadActionLoading = false;
            this.messageService.add({ severity: 'success', summary: 'Lead Assigned', detail: `Vendor #${vendorId} assigned.` });
            this.loadLeads();
            if (this.selectedLead?.id === leadId) this.viewVendors(this.selectedLead);
          },
          error: () => {
            this.leadActionLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Assignment Failed', detail: 'Unable to assign vendor.' });
          }
        });
      }
    });
  }

  selectVendor(leadId: number, vendorId: number): void {
    this.confirmationService.confirm({
      message: `Select vendor #${vendorId} as final vendor for lead #${leadId}?`,
      header: 'Confirm Vendor Selection',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.leadActionLoading = true;
        this.leadService.selectVendor({ leadId, vendorId }).subscribe({
          next: () => {
            this.leadActionLoading = false;
            this.messageService.add({ severity: 'success', summary: 'Vendor Selected', detail: `Vendor #${vendorId} selected.` });
            this.loadLeads();
            if (this.selectedLead?.id === leadId) this.viewVendors(this.selectedLead);
          },
          error: () => {
            this.leadActionLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Selection Failed', detail: 'Unable to select vendor.' });
          }
        });
      }
    });
  }

  closeLead(lead: LeadDTO): void {
    if (!lead?.id || lead.status === 'CLOSED') return;
    this.confirmationService.confirm({
      message: `Close lead #${lead.id}?`,
      header: 'Confirm Close Lead',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.leadActionLoading = true;
        this.leadService.closeLead(lead.id).subscribe({
          next: () => {
            this.leadActionLoading = false;
            this.messageService.add({ severity: 'success', summary: 'Lead Closed', detail: `Lead #${lead.id} is now closed.` });
            this.loadLeads();
          },
          error: () => {
            this.leadActionLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Close Failed', detail: 'Unable to close lead.' });
          }
        });
      }
    });
  }
}
